import { Box } from '@mui/material'
import React from 'react'
import { usePatientFormI18n } from '../utils/patientFormI18n'
import PatientPhoneField from './PatientPhoneField'
import {
  PatientFieldHint,
  PatientInlineAddButton,
  PatientRowDeleteButton,
  PatientTextInput,
} from './PatientFormPrimitives'

/**
 * Compact repeatable rows rendered directly beneath a primary contact field:
 *   [ input ......................... ] [x]
 *   + Add alternate ...
 * Both alternate mobiles and alternate emails use this so they look and behave the same.
 */
export function PatientAlternateContactRows({
  items = [],
  onAdd,
  onUpdate,
  onRemove,
  editMode = true,
  getValidation,
  markTouched,
  touchedPrefix,
  addLabel,
  renderInput,
}) {
  const { copy } = usePatientFormI18n()
  const disabled = !editMode
  const rows = Array.isArray(items) ? items : []

  if (rows.length === 0 && (disabled || !onAdd)) {
    return null
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: rows.length ? '8px' : '4px' }}>
      {rows.map((item, index) => {
        const validation = getValidation?.(index)
        return (
          <Box key={index} sx={{ minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', minWidth: 0 }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                {renderInput({
                  item,
                  index,
                  disabled,
                  invalid: Boolean(validation?.showError),
                  onChange: (value) => {
                    if (!disabled) onUpdate?.(index, value)
                  },
                  onBlur: () => markTouched?.(`${touchedPrefix}.${index}`),
                })}
              </Box>
              {!disabled && (
                <PatientRowDeleteButton
                  label={copy.removeLabel}
                  onClick={() => onRemove?.(index)}
                />
              )}
            </Box>
            {validation?.showError && (
              <PatientFieldHint tone="error">{validation.errorMessage}</PatientFieldHint>
            )}
          </Box>
        )
      })}
      {!disabled && onAdd && (
        <PatientInlineAddButton onClick={onAdd}>{addLabel}</PatientInlineAddButton>
      )}
    </Box>
  )
}

export function PatientAlternativeMobiles({
  alternativeMobiles = [],
  onAdd,
  onUpdate,
  onRemove,
  editMode = true,
  validationHook,
}) {
  const { copy } = usePatientFormI18n()
  return (
    <PatientAlternateContactRows
      items={alternativeMobiles}
      onAdd={onAdd}
      onUpdate={onUpdate}
      onRemove={onRemove}
      editMode={editMode}
      getValidation={validationHook?.getAlternativeMobileValidation}
      markTouched={validationHook?.markTouched}
      touchedPrefix="altMobile"
      addLabel={copy.addAlternateMobileButton}
      renderInput={({ item, disabled, onChange, onBlur }) => (
        <PatientPhoneField
          compact
          value={item || ''}
          onChange={onChange}
          onBlur={onBlur}
          disabled={disabled}
          placeholder={copy.alternateMobilePlaceholder}
        />
      )}
    />
  )
}

export function PatientAlternativeEmails({
  alternativeEmails = [],
  onAdd,
  onUpdate,
  onRemove,
  editMode = true,
  validationHook,
}) {
  const { copy } = usePatientFormI18n()
  return (
    <PatientAlternateContactRows
      items={alternativeEmails}
      onAdd={onAdd}
      onUpdate={onUpdate}
      onRemove={onRemove}
      editMode={editMode}
      getValidation={validationHook?.getAlternativeEmailValidation}
      markTouched={validationHook?.markTouched}
      touchedPrefix="altEmail"
      addLabel={copy.addAlternateEmailButton}
      renderInput={({ item, index, disabled, invalid, onChange, onBlur }) => (
        <PatientTextInput
          type="email"
          inputMode="email"
          autoComplete="off"
          aria-label={`${copy.alternateEmailLabel} ${index + 1}`}
          value={item || ''}
          onChange={(event) => onChange(event.target.value.trim())}
          onBlur={onBlur}
          disabled={disabled}
          invalid={invalid}
          placeholder={copy.alternateEmailPlaceholder}
        />
      )}
    />
  )
}

export default PatientAlternateContactRows
